import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useContext } from 'react';
import { AppContext } from '../context/AppContext';
import { api } from '../api';

const emptyForm = {
  title: '', priority: 'MEDIUM', automation_candidate: false,
  automation_status: 'NOT_AUTOMATED', preconditions: '',
  steps: [], tag_names: [], suite_id: null,
  // Automation guidance (list fields stored as comma-separated strings in the form)
  automation_file: '', automation_notes: '', automation_scope: '',
  reuse_page_objects: '', reuse_fixtures: '', manual_only_steps: '',
  setup_dependencies: '', assertion_guidance: '', test_data_requirements: '',
  related_test_case_ids: '', depends_on_test_case_ids: '',
  business_flow: '', provider: '', feature_area: '', automation_group: '',
};

function formFromTestCase(tc) {
  const arr2str = (arr) => (arr || []).join(', ');
  return {
    title: tc.title || '',
    priority: tc.priority || 'MEDIUM',
    automation_candidate: tc.automation_candidate || false,
    automation_status: tc.automation_status || 'NOT_AUTOMATED',
    preconditions: tc.preconditions || '',
    steps: (tc.steps || []).map((s) => ({ action: s.action, expected_result: s.expected_result || '' })),
    tag_names: (tc.tags || []).map((t) => t.name),
    suite_id: tc.suite_id || null,
    automation_file: tc.automation_file || '',
    automation_notes: tc.automation_notes || '',
    automation_scope: tc.automation_scope || '',
    reuse_page_objects: arr2str(tc.reuse_page_objects),
    reuse_fixtures: arr2str(tc.reuse_fixtures),
    manual_only_steps: arr2str(tc.manual_only_steps),
    setup_dependencies: arr2str(tc.setup_dependencies),
    assertion_guidance: tc.assertion_guidance || '',
    test_data_requirements: tc.test_data_requirements || '',
    related_test_case_ids: arr2str(tc.related_test_case_ids),
    depends_on_test_case_ids: arr2str(tc.depends_on_test_case_ids),
    business_flow: tc.business_flow || '',
    provider: tc.provider || '',
    feature_area: tc.feature_area || '',
    automation_group: tc.automation_group || '',
  };
}

function AutoTextarea({ value, onChange, placeholder }) {
  const ref = useRef(null);
  const resize = useCallback(() => {
    const el = ref.current;
    if (el) { el.style.height = 'auto'; el.style.height = el.scrollHeight + 'px'; }
  }, []);
  useEffect(resize, [value, resize]);
  return (
    <textarea ref={ref} value={value} onChange={onChange} placeholder={placeholder}
      rows={1} className="auto-textarea" />
  );
}

function flattenSuites(suites, level = 0, excludeId = null) {
  const result = [];
  suites.forEach((suite) => {
    if (suite.id === excludeId) return;
    result.push({ ...suite, level });
    if (suite.children) result.push(...flattenSuites(suite.children, level + 1, excludeId));
  });
  return result;
}

export default function TestCaseForm({ isNew }) {
  const { projectId, suiteId, caseId } = useParams();
  const navigate = useNavigate();
  const { refresh } = useContext(AppContext);

  const [form, setForm] = useState(emptyForm);
  const [initialForm, setInitialForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [tagInput, setTagInput] = useState('');
  const [loadedCase, setLoadedCase] = useState(null);
  const [suites, setSuites] = useState([]);
  const [showAuto, setShowAuto] = useState(false);
  const dirtyRef = useRef(false);

  const isDirty = JSON.stringify(form) !== JSON.stringify(initialForm);
  useEffect(() => { dirtyRef.current = isDirty; }, [isDirty]);

  useEffect(() => {
    const handler = (e) => { if (dirtyRef.current) { e.preventDefault(); e.returnValue = ''; } };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, []);

  useEffect(() => {
    if (isNew) {
      setLoadedCase(null);
      setForm(emptyForm);
      setInitialForm(emptyForm);
    } else if (caseId) {
      api.getTestCase(Number(caseId)).then((tc) => {
        setLoadedCase(tc);
        const f = formFromTestCase(tc);
        setForm(f);
        setInitialForm(f);
      });
    }
  }, [caseId, isNew]);

  useEffect(() => {
    if (projectId) {
      api.getSuites(Number(projectId)).then((s) => setSuites(flattenSuites(s)));
    }
  }, [projectId]);

  const backUrl = `/projects/${projectId}/suites/${suiteId}`;
  const set = (field, val) => setForm((prev) => ({ ...prev, [field]: val }));

  const handleStepChange = (i, field, val) => {
    const steps = [...form.steps];
    steps[i] = { ...steps[i], [field]: val };
    setForm((prev) => ({ ...prev, steps }));
  };
  const addStep = () => setForm((prev) => ({ ...prev, steps: [...prev.steps, { action: '', expected_result: '' }] }));
  const removeStep = (i) => setForm((prev) => ({ ...prev, steps: prev.steps.filter((_, idx) => idx !== i) }));
  const moveStep = (i, dir) => {
    const steps = [...form.steps];
    const j = i + dir;
    if (j < 0 || j >= steps.length) return;
    [steps[i], steps[j]] = [steps[j], steps[i]];
    setForm((prev) => ({ ...prev, steps }));
  };

  const addTag = (e) => {
    e.preventDefault();
    const t = tagInput.trim();
    if (t && !form.tag_names.includes(t)) set('tag_names', [...form.tag_names, t]);
    setTagInput('');
  };
  const removeTag = (t) => set('tag_names', form.tag_names.filter((x) => x !== t));

  const handleSave = async () => {
    setSaving(true);
    try {
      const str2list = (s) => (s || '').split(',').map((t) => t.trim()).filter(Boolean);
      const str2ints = (s) => str2list(s).map(Number).filter((n) => !isNaN(n) && n > 0);
      const newSuiteId = form.suite_id || Number(suiteId);

      const payload = {
        title: form.title,
        suite_id: newSuiteId,
        priority: form.priority,
        automation_candidate: form.automation_candidate,
        automation_status: form.automation_status,
        preconditions: form.preconditions || null,
        steps: form.steps,
        tag_names: form.tag_names,
        automation_file: form.automation_file || null,
        automation_notes: form.automation_notes || null,
        automation_scope: form.automation_scope || null,
        reuse_page_objects: str2list(form.reuse_page_objects),
        reuse_fixtures: str2list(form.reuse_fixtures),
        manual_only_steps: str2list(form.manual_only_steps),
        setup_dependencies: str2list(form.setup_dependencies),
        assertion_guidance: form.assertion_guidance || null,
        test_data_requirements: form.test_data_requirements || null,
        related_test_case_ids: str2ints(form.related_test_case_ids),
        depends_on_test_case_ids: str2ints(form.depends_on_test_case_ids),
        business_flow: form.business_flow || null,
        provider: form.provider || null,
        feature_area: form.feature_area || null,
        automation_group: form.automation_group || null,
      };

      if (isNew) {
        await api.createTestCase(payload);
      } else {
        await api.updateTestCase(Number(caseId), payload);
      }
      dirtyRef.current = false;
      navigate(`/projects/${projectId}/suites/${newSuiteId}`);
      refresh();
    } catch (err) {
      alert('Save failed: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this test case?')) return;
    await api.deleteTestCase(Number(caseId));
    dirtyRef.current = false;
    refresh();
    navigate(backUrl);
  };

  const autoFilledCount = [
    form.automation_notes, form.assertion_guidance, form.test_data_requirements,
    form.automation_file, form.automation_scope, form.business_flow,
    form.provider, form.feature_area, form.automation_group,
    form.reuse_page_objects, form.reuse_fixtures, form.setup_dependencies,
    form.manual_only_steps, form.related_test_case_ids, form.depends_on_test_case_ids,
  ].filter(Boolean).length;

  return (
    <div className="tc-form fullscreen-form">
      <div className="form-toolbar">
        <button className="btn-secondary" onClick={() => {
          if (dirtyRef.current && !window.confirm('You have unsaved changes. Discard them?')) return;
          navigate(backUrl);
        }} disabled={saving}>
          ← Back to list
        </button>
        <h3>
          {!isNew && loadedCase?.case_id && <span className="form-case-id">{loadedCase.case_id}</span>}
          {isNew ? 'New Test Case' : 'Edit Test Case'}
        </h3>
        <div className="form-toolbar-actions">
          <button className="btn-primary" onClick={handleSave} disabled={!isDirty || saving}>
            {saving ? 'Saving...' : 'Save'}
          </button>
          {!isNew && <button className="btn-danger" onClick={handleDelete}>Delete</button>}
        </div>
      </div>

      <div className="form-scroll">
        <div className="form-body">
          <div className="form-main">
            <label>Title</label>
            <input type="text" value={form.title} onChange={(e) => set('title', e.target.value)} />

            <label>Preconditions</label>
            <AutoTextarea value={form.preconditions} onChange={(e) => set('preconditions', e.target.value)} placeholder="Enter preconditions..." />

            <div className="steps-section">
              <label>Steps</label>
              {form.steps.length === 0 && <div className="tc-empty" style={{ marginTop: 4 }}>No steps yet</div>}
              {form.steps.map((step, i) => (
                <div className="step-row" key={i}>
                  <span className="step-num">{i + 1}</span>
                  <div className="step-fields">
                    <AutoTextarea placeholder="Action" value={step.action} onChange={(e) => handleStepChange(i, 'action', e.target.value)} />
                    <AutoTextarea placeholder="Expected result" value={step.expected_result} onChange={(e) => handleStepChange(i, 'expected_result', e.target.value)} />
                  </div>
                  <div className="step-buttons">
                    <button onClick={() => moveStep(i, -1)} title="Move up">↑</button>
                    <button onClick={() => moveStep(i, 1)} title="Move down">↓</button>
                    <button onClick={() => removeStep(i)} title="Remove">×</button>
                  </div>
                </div>
              ))}
              <button className="add-step-btn" onClick={addStep}>+ Add Step</button>
            </div>
          </div>

          <div className="form-sidebar">
            {!isNew && (
              <>
                <label>Section</label>
                <select value={form.suite_id || suiteId || ''} onChange={(e) => set('suite_id', Number(e.target.value))}>
                  <option value="">Select suite</option>
                  {suites.map((s) => (
                    <option key={s.id} value={s.id}>{'━ '.repeat(s.level)}{s.name}</option>
                  ))}
                </select>
              </>
            )}

            <label>Priority</label>
            <select value={form.priority} onChange={(e) => set('priority', e.target.value)}>
              <option value="LOW">Low</option>
              <option value="MEDIUM">Medium</option>
              <option value="HIGH">High</option>
            </select>

            <label>Automation Status</label>
            <select value={form.automation_status} onChange={(e) => set('automation_status', e.target.value)}>
              <option value="NOT_AUTOMATED">Not Automated</option>
              <option value="IN_PROGRESS">In Progress</option>
              <option value="AUTOMATED">Automated</option>
            </select>

            <div className="checkbox-row" style={{ marginTop: 8 }}>
              <input type="checkbox" checked={form.automation_candidate} onChange={(e) => set('automation_candidate', e.target.checked)} />
              <label style={{ margin: 0 }}>Automation Candidate</label>
            </div>

            {!isNew && loadedCase?.updated_by && (
              <div className="tc-updated-by">Last edited by: <strong>{loadedCase.updated_by}</strong></div>
            )}

            <label>Tags</label>
            <div className="tags-input">
              {form.tag_names.map((t) => (
                <span className="tag-chip" key={t}>{t}<button onClick={() => removeTag(t)}>×</button></span>
              ))}
              <form onSubmit={addTag} style={{ display: 'inline' }}>
                <input value={tagInput} onChange={(e) => setTagInput(e.target.value)} placeholder="Add tag" />
              </form>
            </div>
          </div>
        </div>

        {/* ── Collapsible Automation Guidance section ───────────────────────── */}
        <div className="auto-section">
          <button className="auto-section-header" onClick={() => setShowAuto((v) => !v)}>
            <span className="auto-section-toggle">{showAuto ? '▾' : '▸'}</span>
            Automation Guidance
            {!showAuto && autoFilledCount > 0 && (
              <span className="auto-badge">{autoFilledCount}</span>
            )}
          </button>

          {showAuto && (
            <div className="auto-grid">
              <div className="auto-col">
                <label>
                  Automation Notes
                  <span className="auto-hint"> — guidance for Claude: what to automate, pitfalls</span>
                </label>
                <AutoTextarea value={form.automation_notes} onChange={(e) => set('automation_notes', e.target.value)}
                  placeholder="e.g. Skip step 3 manually, use fixture auth_user for login..." />

                <label>
                  Assertion Guidance
                  <span className="auto-hint"> — what the generated test should assert</span>
                </label>
                <AutoTextarea value={form.assertion_guidance} onChange={(e) => set('assertion_guidance', e.target.value)}
                  placeholder="e.g. Assert response status 200, payment ID in response..." />

                <label>
                  Test Data Requirements
                  <span className="auto-hint"> — accounts, amounts, currencies needed</span>
                </label>
                <AutoTextarea value={form.test_data_requirements} onChange={(e) => set('test_data_requirements', e.target.value)}
                  placeholder="e.g. GBP account with balance > 100, sandbox API key..." />
              </div>

              <div className="auto-col">
                <label>Business Flow</label>
                <input type="text" value={form.business_flow} onChange={(e) => set('business_flow', e.target.value)} placeholder="e.g. Payment Initiation" />

                <label>Feature Area</label>
                <input type="text" value={form.feature_area} onChange={(e) => set('feature_area', e.target.value)} placeholder="e.g. Payments, Auth, Treasury" />

                <label>Provider</label>
                <input type="text" value={form.provider} onChange={(e) => set('provider', e.target.value)} placeholder="e.g. RailsBank, CurrencyCloud" />

                <label>Automation Group</label>
                <input type="text" value={form.automation_group} onChange={(e) => set('automation_group', e.target.value)} placeholder="e.g. payments_smoke" />

                <label>Automation Scope</label>
                <input type="text" value={form.automation_scope} onChange={(e) => set('automation_scope', e.target.value)} placeholder="smoke | regression | sanity | e2e" />

                <label>Automation File</label>
                <input type="text" value={form.automation_file} onChange={(e) => set('automation_file', e.target.value)} placeholder="tests/auth/test_login.py::test_name" />

                {loadedCase?.automation_last_run && (
                  <>
                    <label>Last Automated Run</label>
                    <div className="auto-last-run">{new Date(loadedCase.automation_last_run).toLocaleString()}</div>
                  </>
                )}
              </div>

              <div className="auto-col-full">
                <div className="auto-list-grid">
                  <div>
                    <label>Reuse Page Objects <span className="auto-hint">comma-separated</span></label>
                    <input type="text" value={form.reuse_page_objects} onChange={(e) => set('reuse_page_objects', e.target.value)} placeholder="LoginPage, BasePage" />
                  </div>
                  <div>
                    <label>Reuse Fixtures <span className="auto-hint">comma-separated</span></label>
                    <input type="text" value={form.reuse_fixtures} onChange={(e) => set('reuse_fixtures', e.target.value)} placeholder="auth_user, test_account" />
                  </div>
                  <div>
                    <label>Setup Dependencies <span className="auto-hint">TC IDs or labels</span></label>
                    <input type="text" value={form.setup_dependencies} onChange={(e) => set('setup_dependencies', e.target.value)} placeholder="TC-1, create_account" />
                  </div>
                  <div>
                    <label>Manual-Only Steps <span className="auto-hint">step numbers</span></label>
                    <input type="text" value={form.manual_only_steps} onChange={(e) => set('manual_only_steps', e.target.value)} placeholder="3, 5" />
                  </div>
                  <div>
                    <label>Related TC IDs <span className="auto-hint">comma-separated</span></label>
                    <input type="text" value={form.related_test_case_ids} onChange={(e) => set('related_test_case_ids', e.target.value)} placeholder="12, 45" />
                  </div>
                  <div>
                    <label>Depends On TC IDs <span className="auto-hint">comma-separated</span></label>
                    <input type="text" value={form.depends_on_test_case_ids} onChange={(e) => set('depends_on_test_case_ids', e.target.value)} placeholder="8, 9" />
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
